import { PlusOutlined } from "@ant-design/icons"
import { Button, Card, Col, Form, Row, Typography, Spin } from "antd"
import { EmptyResourcesIcon } from "icons"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { serialize } from "object-to-formdata"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { getGuideBoats } from "store/boats"
import { collectionSelector, modelSelector } from "store/selectors"
import { getGuideTrip, updateTrip } from "store/trips"
import { AddResourceModal } from "./AddResourceModal"
import { ResourceCard } from "./ResourceCard"
import styles from "./ListingResources.module.css"
import { setFormTouched } from "store/proNewListingForms"

const { Title, Text } = Typography

export const ListingResources = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const boats = useSelector(collectionSelector("boats"))
  const trip = useSelector(modelSelector("trip"))
  const [selectedBoats, setSelectedBoats] = useState(new Set())
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    dispatch(getGuideBoats())
  }, [dispatch])

  useEffect(() => {
    if (trip?.boat_ids) {
      setSelectedBoats(new Set(trip.boat_ids))
      form.setFieldsValue({ boat_ids: Array.from(trip.boat_ids) })
    }
  }, [trip, form])

  const handleSelect = (boatId) => {
    setSelectedBoats((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(boatId)) {
        newSelected.delete(boatId)
      } else {
        newSelected.add(boatId)
      }
      form.setFieldsValue({ boat_ids: Array.from(newSelected) })
      return newSelected
    })
  }

  const showModal = () => {
    setEditingResource(null)
    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
    setEditingResource(null)
  }

  const handleEdit = (boat) => {
    setEditingResource(boat)
    setIsModalVisible(true)
  }

  const onFinish = (callback) => {
    setIsLoading(true)
    const formData = serialize(
      {
        trip: {
          boat_ids: Array.from(selectedBoats).map((id) => Number(id))
        }
      },
      { allowEmptyArrays: true }
    )

    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("resources", true))
        setIsLoading(true)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <Form form={form} className="d-flex flex-column flex-grow-1">
        <div className="d-flex flex-column gap-s mb-3xl">
          <div className="d-flex justify-content-between flex-wrap gap-3 align-items-center">
            <Title level={3} className="mb-0">
              {t("listings.tabs.resources.title")}
            </Title>
            <Button type="primary" size="large" icon={<PlusOutlined />} className={`px-4 ${styles.addResourceBtn}`} onClick={showModal}>
              {t("listings.tabs.resources.add_new_resource")}
            </Button>
          </div>
          <Text type="secondary" className="text-lg">
            {t("listings.tabs.resources.subtitle")}
          </Text>
        </div>

        <Form.Item name="boat_ids" hidden>
          <input type="hidden" />
        </Form.Item>
        <FirstLoading name="boats">
          {boats?.length > 0 ? (
            <Row gutter={[16, 16]} className="mb-3xl">
              {boats.map((boat) => (
                <Col key={boat.id} xs={24} sm={24} md={12}>
                  <ResourceCard
                    name={boat.name}
                    type={t("listings.tabs.resources.boat_type")}
                    selected={selectedBoats.has(boat.id)}
                    onSelect={() => handleSelect(boat.id)}
                    onEdit={() => handleEdit(boat)}
                    properties={boat.properties}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Card className="mb-3xl px-3">
              <div className="d-flex flex-column align-items-center py-2xl px-xl">
                <EmptyResourcesIcon className="mb-l" style={{ width: 100, height: 100 }} />
                <Text className="mb-2">{t("listings.tabs.resources.empty_state.title")}</Text>
                <Text type="secondary" className="mb-l">
                  {t("listings.tabs.resources.empty_state.description")}
                </Text>
                <Button type="primary" onClick={showModal}>
                  {t("listings.tabs.resources.empty_state.action")}
                </Button>
              </div>
            </Card>
          )}
        </FirstLoading>
        <AddResourceModal isVisible={isModalVisible} onCancel={handleModalClose} editingResource={editingResource} />
        <ListingActionButtons form={form} onFinish={onFinish} />
      </Form>
    </Spin>
  )
}
